import { useCallback, useState } from "react";

import { isAPIError, readServiceError } from "@/core/services";
import {
  FornecedorIndisponivelError,
  ignorarConflitoFornecedor,
  remanejarFornecedor,
} from "@/modules/recursos";
import type {
  IgnorarConflitoFornecedorRequest,
  RemanejarFornecedorRequest,
  RemanejarFornecedorResult,
} from "@/modules/recursos";

/** `hub.equipe:editar` é separado do de leitura (`hub.equipe:visualizar`): ver o conflito não basta. */
export const RESOLVER_FORNECEDOR_FORBIDDEN_MESSAGE =
  "Você não tem permissão para resolver conflitos de alocação.";
export const REMANEJAR_FORNECEDOR_NOT_FOUND_MESSAGE =
  "A alocação não foi encontrada. Atualize a carga de trabalho e tente novamente.";
export const REMANEJAR_FORNECEDOR_ERROR_MESSAGE =
  "Não foi possível remanejar o fornecedor. Tente novamente.";
/** RN09 — o conflito já foi tratado por outra pessoa. */
export const IGNORAR_FORNECEDOR_ALREADY_REGISTERED_MESSAGE =
  "Este conflito já foi tratado por outro usuário. Atualize a carga de trabalho.";
export const IGNORAR_FORNECEDOR_ERROR_MESSAGE =
  "Não foi possível registrar o conflito. Tente novamente.";

/** `indisponivel`: 409 `FORNECEDOR_INDISPONIVEL`; `invalid`: 400 (vai no campo justificativa). */
export type ResolverFornecedorFailureKind =
  "indisponivel" | "conflict" | "forbidden" | "notFound" | "invalid" | "error";

export interface ResolverFornecedorFailure {
  ok: false;
  kind: ResolverFornecedorFailureKind;
  message: string;
}

export type ResolverFornecedorResult<T = void> = { ok: true; data: T } | ResolverFornecedorFailure;

interface FailureMessages {
  conflict: string;
  notFound: string;
  error: string;
}

function toFailure(error: unknown, messages: FailureMessages): ResolverFornecedorFailure {
  const failure = (
    kind: ResolverFornecedorFailureKind,
    message: string,
  ): ResolverFornecedorFailure => ({ ok: false, kind, message });

  if (error instanceof FornecedorIndisponivelError) return failure("indisponivel", error.message);
  if (!isAPIError(error)) return failure("error", messages.error);

  switch (error.status) {
    case 409:
      return failure("conflict", messages.conflict);
    case 403:
      return failure("forbidden", RESOLVER_FORNECEDOR_FORBIDDEN_MESSAGE);
    case 404:
      return failure("notFound", messages.notFound);
    case 400:
      // O bff pode embrulhar o 400 do core: vale a mensagem do core.
      return failure("invalid", readServiceError(error).message || messages.error);
    default:
      return failure("error", messages.error);
  }
}

/** Nunca rejeita: devolve o resultado para o modal decidir onde mostrar o erro. */
function useResolverAction<P, R>(action: (params: P) => Promise<R>, messages: FailureMessages) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { conflict, notFound, error: errorMessage } = messages;

  const submit = useCallback(
    async (params: P): Promise<ResolverFornecedorResult<R>> => {
      setIsSubmitting(true);
      try {
        return { ok: true, data: await action(params) };
      } catch (error) {
        return toFailure(error, { conflict, notFound, error: errorMessage });
      } finally {
        setIsSubmitting(false);
      }
    },
    [action, conflict, notFound, errorMessage],
  );

  return { submit, isSubmitting };
}

export interface RemanejarFornecedorParams extends RemanejarFornecedorRequest {
  alocacaoId: string;
}

function remanejar({
  alocacaoId,
  ...body
}: RemanejarFornecedorParams): Promise<RemanejarFornecedorResult> {
  return remanejarFornecedor(alocacaoId, body);
}

export function useRemanejarFornecedor() {
  const { submit, isSubmitting } = useResolverAction(remanejar, {
    conflict: REMANEJAR_FORNECEDOR_ERROR_MESSAGE,
    notFound: REMANEJAR_FORNECEDOR_NOT_FOUND_MESSAGE,
    error: REMANEJAR_FORNECEDOR_ERROR_MESSAGE,
  });
  return { remanejar: submit, isSubmitting };
}

export function useIgnorarConflitoFornecedor() {
  const { submit, isSubmitting } = useResolverAction(
    (params: IgnorarConflitoFornecedorRequest) => ignorarConflitoFornecedor(params),
    {
      conflict: IGNORAR_FORNECEDOR_ALREADY_REGISTERED_MESSAGE,
      notFound: IGNORAR_FORNECEDOR_ERROR_MESSAGE,
      error: IGNORAR_FORNECEDOR_ERROR_MESSAGE,
    },
  );
  return { ignorar: submit, isSubmitting };
}
